import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ARTICLES } from "@/content/blog";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ChatWidget from "@/components/ChatWidget";
import { ArrowRight, Clock } from "lucide-react";

export default function BlogIndex() {
  useEffect(() => {
    document.title = "Trucking Dispatch Blog | Superstate LLC";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        "Practical guides, pricing breakdowns, and strategies for owner-operators — from finding the right truck dispatcher to getting more loads and scaling your trucking business."
      );
    }
  }, []);

  return (
    <div className="relative min-h-screen">
      <Navbar />

      <header className="pt-36 pb-16 md:pt-44 md:pb-20 relative radial-gold">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-gold text-sm uppercase tracking-[0.3em] font-semibold mb-4">
            Superstate Dispatch Blog
          </div>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-white leading-[0.95]">
            TRUCKING <span className="text-gold">INSIGHTS</span>
            <br />
            FOR OWNER-OPERATORS.
          </h1>
          <p className="mt-6 text-white/70 text-base md:text-lg max-w-2xl">
            Straight-talking guides on dispatch, rates, loads, and growing your trucking business.
            Written by the Superstate LLC team — built for drivers, not SEO robots.
          </p>
        </div>
      </header>

      <main className="pb-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 md:gap-10">
            {ARTICLES.map((a, i) => (
              <article
                key={a.slug}
                data-testid={`blog-card-${a.slug}`}
                className="group bg-ink-card border border-ink-line hover:border-gold/50 rounded-lg overflow-hidden transition-all hover:-translate-y-1 hover:shadow-[0_10px_30px_rgba(255,215,0,0.12)]"
              >
                <Link to={`/blog/${a.slug}`} className="grid md:grid-cols-[320px_1fr]">
                  <div className="aspect-video md:aspect-auto bg-ink overflow-hidden">
                    <img
                      src={a.cover}
                      alt={a.coverAlt || a.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading={i < 2 ? "eager" : "lazy"}
                    />
                  </div>
                  <div className="p-6 md:p-8">
                    <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-white/50 mb-3">
                      <span className="text-gold font-semibold">
                        {new Date(a.published).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </span>
                      <span className="w-1 h-1 rounded-full bg-white/30" />
                      <span className="flex items-center gap-1">
                        <Clock size={12} /> {a.readTime}
                      </span>
                    </div>
                    <h2 className="font-heading text-2xl md:text-3xl text-white uppercase leading-tight group-hover:text-gold transition-colors">
                      {a.title}
                    </h2>
                    <p className="mt-3 text-white/65 text-sm md:text-base leading-relaxed">
                      {a.description}
                    </p>
                    <span className="mt-5 inline-flex items-center gap-2 text-gold text-sm font-bold uppercase tracking-wider">
                      Read Article
                      <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </main>

      <Footer />
      <ChatWidget />
    </div>
  );
}
